// @flow strict

import * as React from 'react';
import { FormattedContentBlock, Tooltip } from '@ansarada/ace-react';
import { useTable } from 'react-table';
import debounce from 'lodash.debounce';
import { useSwipeable } from 'react-swipeable';
import { Button } from '../../../internal/Button';
import { CheckMark } from './CheckMark';
import { CrossMark } from './CrossMark';
import { BackArrow } from './BackArrow';
import { Dot } from './Dot';
import { NextArrow } from './NextArrow';
import Currency from '../../../templates/DataPricing/Currency';
import { CURRENCY } from '../../../templates/DataPricing/Currency/currencies';
import Wrapper from './Wrapper';
// eslint-disable-next-line css-modules/no-unused-class
import styles from '../styles.scss';

type TablePropsType = {|
  columns: Array<{ [string]: string | number }>,
  data: Array<{ [string]: string }>,
|};

const getClientWidth = () => {
  return (document && document.body && document.body.clientWidth) || 0;
};

export default (props: TablePropsType) => {
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    totalColumnsWidth,
    allColumns,
    visibleColumns,
    toggleHideAllColumns,
    setHiddenColumns,
  } = useTable({
    columns: props.columns,
    data: props.data,
    autoResetHiddenColumns: false,
  });

  const toggleColumnsWhenResizing = () => {
    const clientWidth = getClientWidth();
    const borderSpacing = clientWidth > 767 ? 8 : 2; // border-spacing responsive in styles.scss
    const additionalSpace = borderSpacing * allColumns.length;
    const offset = clientWidth - totalColumnsWidth - additionalSpace;

    if (offset < 0) {
      const numberOfHiddenColumns = Math.ceil(-offset / 150);
      const hiddenColumnsKeys = [];
      const shownColumns = allColumns.length - numberOfHiddenColumns;
      for (let i = shownColumns > 3 ? shownColumns : 3; i < allColumns.length; i += 1) {
        hiddenColumnsKeys.push(i.toString());
      }
      setHiddenColumns(hiddenColumnsKeys);
    } else {
      toggleHideAllColumns(false);
    }
  };
  const debouncedResizing = debounce(toggleColumnsWhenResizing);

  const swipeableHandlers = (direction: 1 | -1) => {
    const callback = oldHiddenColumns => {
      const visibleColumnsIds = visibleColumns
        .filter(v => v.id !== '0')
        .map(v => parseInt(v.id, 10));
      const firstItem = visibleColumnsIds[0];
      const lastItem = visibleColumnsIds[visibleColumnsIds.length - 1];
      if (direction === 1 && lastItem < allColumns.length - 1) {
        const nextVisibleColumns = visibleColumnsIds.map(i => (i + 1).toString());
        return allColumns
          .filter(c => c.id !== '0' && !nextVisibleColumns.includes(c.id))
          .map(i => i.id);
      }
      if (direction === -1 && firstItem > 1) {
        const nextVisibleColumns = visibleColumnsIds.map(i => (i - 1).toString());
        return allColumns
          .filter(c => c.id !== '0' && !nextVisibleColumns.includes(c.id))
          .map(i => i.id);
      }

      return [...oldHiddenColumns];
    };
    setHiddenColumns(callback);
  };

  const swipeableConfigs = useSwipeable({
    onSwipedLeft: () => swipeableHandlers(1),
    onSwipedRight: () => swipeableHandlers(-1),
    trackMouse: true,
    preventDefaultTouchmoveEvent: true,
  });

  React.useEffect(() => {
    window.addEventListener('resize', debouncedResizing);
    toggleColumnsWhenResizing();

    return () => {
      debouncedResizing.cancel();
      window.removeEventListener('resize', debouncedResizing);
    };
  }, []);

  const onCtaClicked = value => {
    window.location = value;
  };

  const [currency, setCurrency] = React.useState(CURRENCY.AUD);

  React.useEffect(() => {
    const getCurrencyFromUrl = () => {
      const currencyFromUrl = window.location
        .toString()
        .split('/')
        .pop(); // USD, EUR

      switch (currencyFromUrl) {
        case 'AUD':
          return CURRENCY.AUD;
        case 'EUR':
          return CURRENCY.EUR;
        case 'GBP':
          return CURRENCY.GBP;
        case 'USD':
          return CURRENCY.USD;
        case 'ZAR':
          return CURRENCY.ZAR;
        default:
          return null;
      }
    };

    setCurrency(getCurrencyFromUrl());
  }, []);

  const onCurrencyChanged = value => {
    setCurrency(value);
    window.location = value;
  };

  const getCellInfo = cell => {
    const cellValue = cell && cell.value;
    const cellRecommended = cellValue && cellValue.includes('|recommended');
    const cleanCellValue = cellValue && cellValue.replace('|recommended', '');
    const isMultipleValues = cleanCellValue && cleanCellValue.includes('|');
    const splitValue = isMultipleValues && cleanCellValue.split('|');

    // CTA sample value
    // cta|$499|Simple and secure document sharing and small projects.|Get a quote and free data room.||quote?DataPlan=250%20MB&SubscriptionPlan=Monthly|Default|Small
    const isCta = isMultipleValues && cleanCellValue.toLowerCase().startsWith('cta');
    const cellText = isCta && splitValue[1];
    const ctaTooltipLine1 = isCta && splitValue[2];
    const ctaTooltipLine2 = isCta && splitValue[3];
    const ctaTooltipLine3 = isCta && splitValue[4];
    const ctaHasTooltip = ctaTooltipLine1 || ctaTooltipLine2 || ctaTooltipLine3;
    const ctaTarget = isCta && splitValue[5];
    const ctaVariant = isCta && splitValue[6];
    const ctaSize = isCta && splitValue[7];

    const isMultiline = isMultipleValues && cleanCellValue.toLowerCase().startsWith('multiline');
    const multilineHeader = isMultiline && splitValue[1];
    const multilineDetails = isMultiline && splitValue[2];

    return {
      cellRecommended,
      cleanCellValue,
      isCta,
      isMultiline,
      multilineHeader,
      ctaHasTooltip,
      cellText,
      ctaTooltipLine1,
      ctaTooltipLine2,
      ctaTooltipLine3,
      ctaTarget,
      ctaVariant,
      ctaSize,
      multilineDetails,
    };
  };

  return (
    <>
      {allColumns.length !== visibleColumns.length && (
        <div className={styles.navigation}>
          <Wrapper onClick={() => swipeableHandlers(-1)}>
            <BackArrow />
          </Wrapper>
          {allColumns
            .filter(a => a.id !== '0')
            .map(c => (
              <Wrapper isDisabled>
                <Dot active={visibleColumns.some(v => v.id === c.id)} />
              </Wrapper>
            ))}
          <Wrapper onClick={() => swipeableHandlers(1)}>
            <NextArrow />
          </Wrapper>
        </div>
      )}
      <table {...getTableProps()} {...swipeableConfigs}>
        <thead>
          {headerGroups.map(headerGroup => (
            <tr {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column, index) => {
                return (
                  <>
                    {currency &&
                      index === 1 &&
                      column
                        .render('Header')
                        .toString()
                        .includes('|useCurrencySelector') && (
                        <th>
                          <div className={styles.currencyWrapper}>
                            <Currency currency={currency} onSelect={onCurrencyChanged} />
                          </div>
                        </th>
                      )}
                    {(!currency ||
                      !column
                        .render('Header')
                        .toString()
                        .includes('|useCurrencySelector')) &&
                      index === 1 && <th />}
                    {index > 0 && (
                      <th
                        {...column.getHeaderProps()}
                        className={
                          column
                            .render('Header')
                            .toString()
                            .includes('|recommended') && styles.primary
                        }
                      >
                        {column
                          .render('Header')
                          .toString()
                          .replace('|useCurrencySelector', '')
                          .replace('|recommended', '')}
                      </th>
                    )}
                  </>
                );
              })}
            </tr>
          ))}
        </thead>
        <tbody {...getTableBodyProps()}>
          {rows.map(row => {
            prepareRow(row);
            return (
              <tr {...row.getRowProps()}>
                {row.cells.map(cell => {
                  const {
                    cellRecommended,
                    cleanCellValue,
                    isCta,
                    isMultiline,
                    multilineHeader,
                    ctaHasTooltip,
                    cellText,
                    ctaTooltipLine1,
                    ctaTooltipLine2,
                    ctaTooltipLine3,
                    ctaTarget,
                    ctaVariant,
                    ctaSize,
                    multilineDetails,
                  } = getCellInfo(cell);

                  return (
                    <td {...cell.getCellProps()} className={cellRecommended && styles.highlighted}>
                      {cleanCellValue && cleanCellValue.toLowerCase() === 'yes' && <CheckMark />}
                      {cleanCellValue && cleanCellValue.toLowerCase() === 'no' && <CrossMark />}

                      {isMultiline && (
                        <>
                          <div className={styles.multilineHeaderWrapper}>{multilineHeader}</div>
                          {multilineDetails}
                        </>
                      )}

                      {ctaHasTooltip && (
                        <Tooltip
                          id={cellText.replace('$', '_')}
                          defaultOpen={false}
                          position="Top"
                          text={
                            <FormattedContentBlock>
                              {ctaTooltipLine1 && (
                                <>
                                  {ctaTooltipLine1}
                                  <br />
                                </>
                              )}
                              {ctaTooltipLine2 && (
                                <>
                                  {ctaTooltipLine2}
                                  <br />
                                </>
                              )}
                              {ctaTooltipLine3 && <>{ctaTooltipLine3}</>}
                            </FormattedContentBlock>
                          }
                        >
                          <Button
                            onClick={() => onCtaClicked(ctaTarget)}
                            variant={ctaVariant}
                            size={ctaSize}
                          >
                            {cellText}
                          </Button>
                        </Tooltip>
                      )}

                      {isCta && !ctaHasTooltip && (
                        <Button
                          onClick={() => onCtaClicked(ctaTarget)}
                          variant={ctaVariant}
                          size={ctaSize}
                        >
                          {cellText}
                        </Button>
                      )}

                      {!cleanCellValue ||
                        (cleanCellValue.toLowerCase() !== 'yes' &&
                          cleanCellValue.toLowerCase() !== 'no' &&
                          !isCta &&
                          !isMultiline &&
                          cleanCellValue)}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </>
  );
};
